import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calculator, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function ExchangeCalculatorPage() {
  const { t } = useLanguage();
  const [amount, setAmount] = useState('100000');
  const [calculation, setCalculation] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);

  useEffect(() => {
    fetchExchangeRate();
  }, []);

  useEffect(() => {
    if (amount && parseFloat(amount) > 0) {
      calculateAmount();
    }
  }, [amount]);

  const fetchExchangeRate = async () => {
    try {
      const response = await api.getExchangeRate();
      setExchangeRate(response.data);
    } catch (error) {
      console.error('Failed to fetch exchange rate:', error);
    }
  };

  const calculateAmount = async () => {
    try {
      const response = await api.calculateExchange(parseFloat(amount));
      setCalculation(response.data);
    } catch (error) {
      console.error('Failed to calculate:', error);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl" data-testid="exchange-calculator-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">
            為替計算機
          </h1>
          <p className="text-muted-foreground">
            {t('calc_subtitle')}
          </p>
        </div>

        <div className="bg-card border border-border/50 rounded-2xl p-8 mb-8">
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="amount">{t('calc_amount_label')}</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium text-xl">
                  ¥
                </span>
                <Input
                  id="amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-10 h-16 text-2xl font-mono"
                  min="1000"
                  data-testid="calculator-amount-input"
                />
              </div>
            </div>

            {calculation && (
              <>
                <div className="flex items-center justify-center py-4">
                  <ArrowRight className="h-8 w-8 text-accent" />
                </div>

                <div className="bg-accent/5 border border-accent/20 rounded-xl p-6 space-y-4" data-testid="calculation-display">
                  <div className="text-center">
                    <div className="text-sm text-muted-foreground mb-2">{t('send_receive')}</div>
                    <div className="text-5xl font-bold font-mono text-accent">
                      R$ {calculation.amount_brl.toLocaleString()}
                    </div>
                  </div>

                  <div className="border-t border-accent/20 pt-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('calc_amount')}</span>
                      <span className="font-mono font-medium">¥{calculation.amount_jpy.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('send_fee')}</span>
                      <span className="font-mono font-medium">¥{calculation.fee_jpy.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-accent/20">
                      <span className="text-muted-foreground font-medium">{t('send_total')}</span>
                      <span className="font-mono font-bold text-lg">¥{calculation.total_jpy.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {exchangeRate && (
          <div className="bg-secondary/30 rounded-2xl p-6" data-testid="rate-info">
            <h3 className="font-heading text-lg font-semibold text-primary mb-4">
              {t('calc_rate_title')}
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('calc_market_rate')}</span>
                <span className="font-mono font-medium">1 JPY = {exchangeRate.rate.toFixed(5)} BRL</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('calc_markup')}</span>
                <span className="font-mono font-medium">{(exchangeRate.markup * 100).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-border">
                <span className="text-muted-foreground font-medium">{t('calc_applied_rate')}</span>
                <span className="font-mono font-bold">1 JPY = {exchangeRate.final_rate.toFixed(5)} BRL</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              {t('calc_note')}
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
